/**
 * Confirmação antes de remover uma linha de lançamento.
 *
 * A regra é: confirma quando há o que perder, e só nesses casos.
 *
 * Confirmar TODA remoção seria pior que não confirmar nenhuma. A pessoa
 * adiciona uma linha por engano, tenta tirar, e leva um aviso; na terceira vez
 * ela para de ler e passa a clicar "OK" por reflexo — aí o aviso não protege
 * mais nada, justamente na vez em que havia 15 embriões na linha.
 *
 * Por isso `descreverPerda` devolve null quando a linha está vazia: nesse caso
 * a remoção acontece direto, sem pergunta.
 */

const plural = (n, um, muitos) => `${n} ${n === 1 ? um : muitos}`;

/** Lista de perdas de uma doadora da FIV, em ordem de gravidade. */
export const perdasDaDoadoraFIV = (d = {}) => {
  const num = (v) => parseInt(v) || 0;
  const embrioes = num(d.stage_bi) + num(d.stage_bl) + num(d.stage_bx)
                 + num(d.stage_bn) + num(d.stage_be);
  const perdas = [];

  if (embrioes > 0) perdas.push(plural(embrioes, 'embrião', 'embriões'));
  if (num(d.cleaved_oocytes) > 0) perdas.push(`${d.cleaved_oocytes} clivados`);
  if (num(d.viable_oocytes) > 0)  perdas.push(`${d.viable_oocytes} oócitos viáveis`);
  if (num(d.gotas) > 0)           perdas.push(plural(num(d.gotas), 'gota', 'gotas'));

  return perdas;
};

/** Lista de perdas de uma doadora da OPU. */
export const perdasDaDoadoraOPU = (d = {}) => {
  const num = (v) => parseInt(v) || 0;
  const perdas = [];
  if (num(d.oocytes_recovered) > 0) perdas.push(`${d.oocytes_recovered} oócitos`);
  else if (num(d.oocytes_viable) > 0) perdas.push(`${d.oocytes_viable} viáveis`);
  if ((d.observations || '').trim()) perdas.push('observações');
  return perdas;
};

/**
 * Monta o texto da confirmação, ou devolve null quando não há o que perder.
 *
 * `nome` é como a linha é conhecida na tela (brinco da doadora). Sem ele o
 * aviso vira "remover esta linha?", que não ajuda quem tem dez linhas iguais
 * na frente.
 */
export const descreverPerda = ({ nome, perdas = [], aviso = null }) => {
  if (perdas.length === 0) return null;

  const quem = nome ? `"${nome}"` : 'esta linha';
  const linhas = [
    `Remover ${quem}?`,
    '',
    `Vai junto: ${perdas.join(', ')}.`,
  ];
  if (aviso) linhas.push('', aviso);
  return linhas.join('\n');
};

/**
 * Pergunta e devolve se pode seguir.
 *
 * Isolado do componente para ser testável sem montar tela, e para que a troca
 * de `window.confirm` por um diálogo próprio aconteça num lugar só.
 */
export const confirmarRemocao = (descricao, perguntar = window.confirm) =>
  descricao === null ? true : perguntar(descricao);
